import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  X, 
  Calendar as CalendarIcon,
  Clock,
  User,
  Car,
  Wrench,
  Users as UsersIcon,
  FileText,
  AlertCircle
} from 'lucide-react';
import { supabase } from '@lib/supabaseClient';
import { useNavigate } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const Calendar = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEventModal, setShowEventModal] = useState(false);

  // Charger les événements depuis la vue combinée
  useEffect(() => {
    loadEvents();
  }, [currentDate]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      // Charger depuis la vue qui combine interventions + calendar_events
      const { data, error } = await supabase
        .from('calendar_all_events')
        .select('*')
        .gte('event_date', format(startOfMonth(currentDate), 'yyyy-MM-dd'))
        .lte('event_date', format(endOfMonth(currentDate), 'yyyy-MM-dd'))
        .order('event_date', { ascending: true })
        .order('event_time', { ascending: true });

      if (error) {
        console.error('Erreur chargement événements:', error);
        // Si la vue n'existe pas encore, charger depuis les deux tables séparément
        await loadEventsFromTables();
      } else {
        setEvents(data || []);
      }
    } catch (error) {
      console.error('Erreur:', error);
      await loadEventsFromTables();
    } finally {
      setLoading(false);
    }
  };

  // Fallback: charger depuis les deux tables séparément
  const loadEventsFromTables = async () => {
    try {
      // Charger les interventions
      const { data: interventions, error: intError } = await supabase
        .from('interventions')
        .select('*')
        .gte('date_debut', format(startOfMonth(currentDate), 'yyyy-MM-dd'))
        .lte('date_debut', format(endOfMonth(currentDate), 'yyyy-MM-dd'));

      // Charger les événements du calendrier
      const { data: calendarEvents, error: calError } = await supabase
        .from('calendar_events')
        .select('*')
        .gte('date', format(startOfMonth(currentDate), 'yyyy-MM-dd'))
        .lte('date', format(endOfMonth(currentDate), 'yyyy-MM-dd'));

      if (intError) console.error('Erreur interventions:', intError);
      if (calError) console.error('Erreur calendar_events:', calError);

      // Formater et combiner
      const formattedInterventions = (interventions || []).map(int => ({
        source_type: 'intervention',
        event_id: int.id,
        event_date: int.date_debut,
        event_time: int.date_debut_heure,
        event_end_date: int.date_fin,
        event_end_time: int.date_fin_heure,
        title: `${int.client_name || 'Client'} - ${int.immatriculation || 'N/A'}`,
        type: 'intervention',
        description: int.client_name,
        immatriculation: int.immatriculation,
        section: int.intervention_type,
        formateur: null,
        created_at: int.created_at
      }));

      const formattedEvents = (calendarEvents || []).map(evt => ({
        source_type: 'event',
        event_id: evt.id,
        event_date: evt.date,
        event_time: evt.time,
        event_end_date: evt.date,
        event_end_time: evt.time,
        title: evt.title,
        type: evt.type,
        description: evt.description,
        immatriculation: evt.immatriculation,
        section: evt.section,
        formateur: evt.formateur,
        created_at: evt.created_at
      }));

      setEvents([...formattedInterventions, ...formattedEvents]);
    } catch (error) {
      console.error('Erreur chargement fallback:', error);
    }
  };

  // Générer les jours du mois
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Obtenir le jour de la semaine du premier jour (0 = dimanche, 1 = lundi, etc.)
  const firstDayOfWeek = monthStart.getDay();
  const startPadding = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Lundi = 0

  // Ajouter des jours vides au début
  const calendarDays = [
    ...Array(startPadding).fill(null),
    ...monthDays
  ];

  // Obtenir les événements pour un jour donné
  const getEventsForDay = (day) => {
    if (!day) return [];
    return events.filter(event => {
      const eventDate = parseISO(event.event_date);
      return isSameDay(eventDate, day);
    });
  };

  // Navigation mois
  const previousMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const nextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const goToToday = () => setCurrentDate(new Date());

  // Clic sur un jour
  const handleDayClick = (day) => {
    if (!day) return;
    setSelectedDate(day);
    const dayEvents = getEventsForDay(day);
    if (dayEvents.length > 0) {
      setShowEventModal(true);
    } else {
      // Aucun événement, proposer de créer une intervention
      navigate('/pole-auto/interventions/new', { 
        state: { selectedDate: format(day, 'yyyy-MM-dd') } 
      });
    }
  };

  // Clic sur un événement
  const handleEventClick = (event, e) => {
    e.stopPropagation();
    setSelectedEvent(event);
  };

  // Fermer les modals
  const closeModals = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
    setSelectedDate(null);
  };

  // Couleur selon le type
  const getEventColor = (type, sourceType) => {
    if (sourceType === 'intervention') {
      return 'bg-blue-500 text-white border-blue-600';
    }
    switch (type) {
      case 'reunion':
        return 'bg-purple-500 text-white border-purple-600';
      case 'event':
        return 'bg-green-500 text-white border-green-600';
      case 'intervention':
        return 'bg-blue-500 text-white border-blue-600';
      default:
        return 'bg-gray-500 text-white border-gray-600';
    }
  };

  // Icône selon le type
  const getEventIcon = (type, sourceType) => {
    if (sourceType === 'intervention') {
      return <Wrench className="w-3 h-3" />;
    }
    switch (type) {
      case 'reunion':
        return <UsersIcon className="w-3 h-3" />;
      case 'intervention':
        return <Wrench className="w-3 h-3" />;
      default:
        return <CalendarIcon className="w-3 h-3" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-white mb-2 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500">
            Calendrier des Interventions & Réunions
          </h1>
          <p className="text-gray-300">
            Planification et suivi des véhicules pédagogiques
          </p>
        </motion.div>

        {/* Navigation du calendrier */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-800/80 backdrop-blur-sm rounded-xl shadow-xl p-6 mb-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={previousMonth}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              
              <h2 className="text-2xl font-bold text-white min-w-[200px] text-center">
                {format(currentDate, 'MMMM yyyy', { locale: fr })}
              </h2>
              
              <button
                onClick={nextMonth}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={goToToday}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Aujourd'hui
              </button>
              
              <button
                onClick={() => navigate('/pole-auto/interventions/new')}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Nouvelle intervention</span>
              </button>
            </div>
          </div>

          {/* Jours de la semaine */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day) => (
              <div
                key={day}
                className="text-center text-sm font-semibold text-gray-400 py-2"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Grille du calendrier */}
          <div className="grid grid-cols-7 gap-2">
            {calendarDays.map((day, index) => {
              const dayEvents = day ? getEventsForDay(day) : [];
              const isToday = day && isSameDay(day, new Date());
              const isSelected = day && selectedDate && isSameDay(day, selectedDate);

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.01 }}
                  onClick={() => handleDayClick(day)}
                  className={`
                    min-h-[100px] p-2 rounded-lg border-2 cursor-pointer transition-all
                    ${day ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-transparent border-transparent'}
                    ${isToday ? 'border-blue-500 bg-blue-900/30' : 'border-slate-600'}
                    ${isSelected ? 'border-purple-500 bg-purple-900/30' : ''}
                    ${!isSameMonth(day || new Date(), currentDate) ? 'opacity-50' : ''}
                  `}
                >
                  {day && (
                    <>
                      <div className={`
                        text-sm font-semibold mb-1
                        ${isToday ? 'text-blue-400' : 'text-white'}
                      `}>
                        {format(day, 'd')}
                      </div>
                      
                      {/* Événements du jour */}
                      <div className="space-y-1">
                        {dayEvents.slice(0, 2).map((event, idx) => (
                          <motion.div
                            key={`${event.event_id}-${idx}`}
                            whileHover={{ scale: 1.05 }}
                            onClick={(e) => handleEventClick(event, e)}
                            className={`
                              text-xs p-1 rounded border flex items-center space-x-1
                              ${getEventColor(event.type, event.source_type)}
                              hover:shadow-lg transition-all
                            `}
                          >
                            {getEventIcon(event.type, event.source_type)}
                            <span className="truncate flex-1">
                              {event.event_time && format(parseISO(`2000-01-01T${event.event_time}`), 'HH:mm')}
                              {' - '}
                              {event.title.substring(0, 15)}
                            </span>
                          </motion.div>
                        ))}
                        
                        {dayEvents.length > 2 && (
                          <div className="text-xs text-blue-400 text-center">
                            +{dayEvents.length - 2} autre{dayEvents.length - 2 > 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </motion.div>
              );
            })}
          </div>

          {loading && (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          )}
        </motion.div>

        {/* Légende */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-slate-800/80 backdrop-blur-sm rounded-xl shadow-xl p-4"
        >
          <h3 className="text-white font-semibold mb-3">Légende</h3>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-blue-500 rounded"></div>
              <span className="text-sm text-gray-300">Intervention</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-purple-500 rounded"></div>
              <span className="text-sm text-gray-300">Réunion</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-green-500 rounded"></div>
              <span className="text-sm text-gray-300">Événement</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Modal liste événements du jour */}
      <AnimatePresence>
        {showEventModal && selectedDate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={closeModals}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-slate-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
            >
              <div className="p-6 border-b border-slate-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-white">
                    {format(selectedDate, 'EEEE d MMMM yyyy', { locale: fr })}
                  </h3>
                  <button
                    onClick={closeModals}
                    className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>
              </div>

              <div className="p-6 overflow-y-auto max-h-[60vh]">
                <div className="space-y-4">
                  {getEventsForDay(selectedDate).map((event, idx) => (
                    <motion.div
                      key={`${event.event_id}-${idx}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className={`
                        p-4 rounded-lg border-l-4 cursor-pointer
                        ${getEventColor(event.type, event.source_type)}
                        hover:shadow-lg transition-all
                      `}
                      onClick={() => {
                        if (event.source_type === 'intervention') {
                          navigate(`/pole-auto/interventions/${event.event_id}`);
                        }
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          <div className="mt-1">
                            {getEventIcon(event.type, event.source_type)}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-lg mb-1">
                              {event.title}
                            </h4>
                            
                            <div className="space-y-1 text-sm opacity-90">
                              {event.event_time && (
                                <div className="flex items-center space-x-2">
                                  <Clock className="w-4 h-4" />
                                  <span>
                                    {format(parseISO(`2000-01-01T${event.event_time}`), 'HH:mm')}
                                    {event.event_end_time && event.event_end_time !== event.event_time && (
                                      <> - {format(parseISO(`2000-01-01T${event.event_end_time}`), 'HH:mm')}</>
                                    )}
                                  </span>
                                </div>
                              )}
                              
                              {event.description && (
                                <div className="flex items-center space-x-2">
                                  <User className="w-4 h-4" />
                                  <span>{event.description}</span>
                                </div>
                              )}
                              
                              {event.immatriculation && (
                                <div className="flex items-center space-x-2">
                                  <Car className="w-4 h-4" />
                                  <span>{event.immatriculation}</span>
                                </div>
                              )}
                              
                              {event.section && (
                                <div className="flex items-center space-x-2">
                                  <FileText className="w-4 h-4" />
                                  <span>{event.section}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <button
                  onClick={() => navigate('/pole-auto/interventions/new', { 
                    state: { selectedDate: format(selectedDate, 'yyyy-MM-dd') } 
                  })}
                  className="w-full mt-4 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <Plus className="w-5 h-5" />
                  <span>Ajouter une intervention ce jour</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal détail événement */}
      <AnimatePresence>
        {selectedEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={closeModals}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-slate-800 rounded-xl shadow-2xl max-w-lg w-full"
            >
              <div className={`p-6 border-b border-slate-700 ${getEventColor(selectedEvent.type, selectedEvent.source_type)}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getEventIcon(selectedEvent.type, selectedEvent.source_type)}
                    <h3 className="text-2xl font-bold">
                      {selectedEvent.title}
                    </h3>
                  </div>
                  <button
                    onClick={closeModals}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4 text-white">
                <div className="flex items-center space-x-3">
                  <CalendarIcon className="w-5 h-5 text-blue-400" />
                  <span>
                    {format(parseISO(selectedEvent.event_date), 'EEEE d MMMM yyyy', { locale: fr })}
                  </span>
                </div>

                {selectedEvent.event_time && (
                  <div className="flex items-center space-x-3">
                    <Clock className="w-5 h-5 text-blue-400" />
                    <span>
                      {format(parseISO(`2000-01-01T${selectedEvent.event_time}`), 'HH:mm')}
                      {selectedEvent.event_end_time && selectedEvent.event_end_time !== selectedEvent.event_time && (
                        <> - {format(parseISO(`2000-01-01T${selectedEvent.event_end_time}`), 'HH:mm')}</>
                      )}
                    </span>
                  </div>
                )}

                {selectedEvent.description && (
                  <div className="flex items-start space-x-3">
                    <User className="w-5 h-5 text-blue-400 mt-0.5" />
                    <div>
                      <p className="font-medium">Client</p>
                      <p className="text-gray-300">{selectedEvent.description}</p>
                    </div>
                  </div>
                )}

                {selectedEvent.immatriculation && (
                  <div className="flex items-start space-x-3">
                    <Car className="w-5 h-5 text-blue-400 mt-0.5" />
                    <div>
                      <p className="font-medium">Véhicule</p>
                      <p className="text-gray-300">{selectedEvent.immatriculation}</p>
                    </div>
                  </div>
                )}

                {selectedEvent.section && (
                  <div className="flex items-start space-x-3">
                    <FileText className="w-5 h-5 text-blue-400 mt-0.5" />
                    <div>
                      <p className="font-medium">Section</p>
                      <p className="text-gray-300">{selectedEvent.section}</p>
                    </div>
                  </div>
                )}

                {selectedEvent.source_type === 'intervention' && (
                  <button
                    onClick={() => navigate(`/pole-auto/interventions/${selectedEvent.event_id}`)}
                    className="w-full mt-4 px-4 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                  >
                    Voir l'intervention complète
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Calendar;